import { useCallback, useEffect, useState } from 'react';
import { Api } from '../../application/panelApi';
import { modal } from '../../components/ModalHost';
import { errText, fmtDate } from '../../lib/format';
import { personaRows, type Donor, type Persona } from './model';

type PersonaResponse = {
  profile?: {
    phase?: string;
    status?: string;
    createdAt?: string;
    lastRunAt?: string;
    runCount?: number;
    farmDays?: number;
    searchEngine?: string;
  };
  persona?: Persona | null;
  donor?: Donor | null;
  chromium?: number | null;
  engineChromium?: number;
};


type Bridge = { reload: () => void };

function ProfileCardBody({
  cid,
  profileId,
  engineChromium,
  bridge,
}: {
  cid: string;
  profileId: string;
  engineChromium: number;
  bridge: Bridge;
}) {
  const [tab, setTab] = useState<'fp' | 'log'>('fp');
  const [fp, setFp] = useState<Array<[string, string]> | string>('Загрузка…');
  const [log, setLog] = useState('Загрузка…');

  const loadLog = useCallback(async () => {
    try {
      const data = (await Api.clusters.profileLog(cid, profileId)) as { log?: string };
      const text = (data.log || '').trim();
      setLog(text || 'Лог пуст.');
    } catch (err) {
      setLog(errText(err));
    }
  }, [cid, profileId]);

  useEffect(() => {
    bridge.reload = () => void loadLog();
    void loadLog();
  }, [bridge, loadLog]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const data = (await Api.clusters.profilePersona(cid, profileId)) as PersonaResponse;
        if (!alive) return;
        const p = data.profile ?? {};
        const engine = data.engineChromium ?? engineChromium;
        const chromium =
          data.chromium == null
            ? 'не открывался'
            : `${data.chromium}${data.chromium > (data.engineChromium ?? 0) ? ' (новее движка — не откроется)' : ''}`;
        const rows: Array<[string, string]> = [
          ['Фаза / статус', `${p.phase} / ${p.status}`],
          ['Создан профиль', p.createdAt ? `${fmtDate(p.createdAt)} UTC` : '—'],
          ['Последний запуск', p.lastRunAt ? `${fmtDate(p.lastRunAt)} UTC` : 'не запускался'],
          ['Прогонов / дней', `${p.runCount ?? 0} / ${p.farmDays ?? 0}`],
          ['Поисковик', p.searchEngine || '—'],
          ['Chromium профиля', chromium],
          ...personaRows(data.persona, data.donor, engine),
        ];
        setFp(rows);
      } catch (err) {
        if (alive) setFp(errText(err));
      }
    })();
    return () => {
      alive = false;
    };
  }, [cid, profileId, engineChromium]);

  return (
    <>
      <div className="tabs" id="pcTabs">
        <button type="button" className={`tab${tab === 'fp' ? ' active' : ''}`} data-pc="fp" onClick={() => setTab('fp')}>
          Отпечаток
        </button>
        <button type="button" className={`tab${tab === 'log' ? ' active' : ''}`} data-pc="log" onClick={() => setTab('log')}>
          Лог прогрева
        </button>
      </div>
      <div data-pcpane="fp" className={tab === 'fp' ? undefined : 'hidden'}>
        <div className="pr-kv" id="pcFp" style={{ marginTop: 12 }}>
          {typeof fp === 'string'
            ? fp
            : fp.map(([k, v]) => (
                <KvRow key={k} k={k} v={v} />
              ))}
        </div>
      </div>
      <div data-pcpane="log" className={tab === 'log' ? undefined : 'hidden'}>
        <pre id="plBody" className="pr-log" style={{ maxHeight: '60vh' }}>
          {log}
        </pre>
      </div>
    </>
  );
}

function KvRow({ k, v }: { k: string; v: string }) {
  return (
    <>
      <div className="k">{k}</div>
      <div className="v">{v}</div>
    </>
  );
}


export function openProfileCard(
  cid: string,
  profileId: string,
  engineChromium: number,
): void {
  const bridge: Bridge = { reload: () => undefined };
  modal(
    `Профиль ${profileId}`,
    <ProfileCardBody cid={cid} profileId={profileId} engineChromium={engineChromium} bridge={bridge} />,
    <>
      <button type="button" className="btn" id="plRefresh" onClick={() => bridge.reload()}>
        Обновить лог
      </button>
      <button type="button" className="btn" data-close>
        Закрыть
      </button>
    </>,
    true,
  );
}
